<template>
  <div class="bg-gray-50 h-full min-h-0 overflow-y-auto p-3 md:p-4">
    <div class="max-w-7xl mx-auto space-y-3">
      <AlertMessage
        v-if="alertMessage"
        :message="alertMessage"
        :alertType="alertType"
        @close="clearAlert"
      />

      <section class="grid grid-cols-2 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <SummaryMetricCard
          v-for="card in metricCards"
          :key="card.label"
          :label="card.label"
          :value="card.value"
          :suffix="card.suffix"
          :percentage="card.percentage"
          :color="card.color"
        />
      </section>

      <section class="grid gap-3 xl:grid-cols-2">
        <div class="bg-white rounded-3xl shadow-sm p-4">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-base font-semibold text-primary-dark-text">
              Latest Polls
            </h2>
            <button
              type="button"
              class="text-xs md:text-xs text-primary hover:text-primary-dark transition disabled:opacity-60 px-3 py-2 md:px-0 md:py-0 rounded-md md:rounded-none bg-primary/5 md:bg-transparent font-medium md:font-normal"
              :disabled="isLoading"
              @click="viewAllPolls"
            >
              View all
            </button>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr
                  class="text-left text-[10px] uppercase tracking-wide text-neutral-light"
                >
                  <th class="py-1 whitespace-nowrap">S/N</th>
                  <th class="py-1">Name</th>
                  <th class="py-1 hidden md:table-cell">Created At</th>
                  <th class="py-1">Type</th>
                  <th class="py-1">Status</th>
                </tr>
              </thead>
              <tbody class="text-xs text-primary-dark-text">
                <template v-if="isLoading">
                  <tr>
                    <td
                      colspan="4"
                      class="py-3 text-center text-neutral-light md:colspan-5"
                    >
                      Loading latest polls…
                    </td>
                  </tr>
                </template>
                <template v-else-if="latestPolls.length">
                  <tr
                    v-for="(poll, index) in latestPolls"
                    :key="poll.id"
                    class="border-t border-gray-100 hover:bg-gray-50/70 transition"
                  >
                    <td class="py-1.5 font-medium whitespace-nowrap">
                      {{ formatSerial(index) }}
                    </td>
                    <td class="py-1.5">{{ poll.name }}</td>
                    <td class="py-1.5 hidden md:table-cell">
                      {{ formatDate(poll.createdAt) }}
                    </td>
                    <td class="py-1.5">{{ poll.type }}</td>
                    <td class="py-1.5">
                      <span
                        :class="[
                          'px-2.5 py-1 rounded-full text-[11px] font-medium inline-flex items-center gap-1 whitespace-nowrap',
                          isApprovedStatus(poll.status)
                            ? 'bg-emerald-50 text-emerald-500'
                            : 'bg-amber-50 text-amber-500',
                        ]"
                      >
                        <span
                          class="h-2 w-2 rounded-full flex-shrink-0"
                          :class="
                            isApprovedStatus(poll.status)
                              ? 'bg-emerald-500'
                              : 'bg-amber-500'
                          "
                        ></span>
                        {{ statusLabel(poll.status) }}
                      </span>
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="4" class="py-6 md:colspan-5">
                    <div
                      class="flex flex-col items-center justify-center gap-1.5 text-center text-xs text-neutral-light"
                    >
                      <svg
                        class="h-8 w-8 text-neutral-light/60"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M4 7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z"
                        />
                        <path d="M10 3v4" />
                        <path d="M14 3v4" />
                        <path d="M8 12h8" />
                        <path d="M8 16h5" />
                      </svg>
                      <p class="font-medium text-primary-dark-text">
                        No polls yet
                      </p>
                      <p>Newly created polls will appear in this table.</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="bg-white rounded-3xl shadow-sm p-4">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-base font-semibold text-primary-dark-text">
              Latest Spaces
            </h2>
            <button
              type="button"
              class="text-xs md:text-xs text-primary hover:text-primary-dark transition disabled:opacity-60 px-3 py-2 md:px-0 md:py-0 rounded-md md:rounded-none bg-primary/5 md:bg-transparent font-medium md:font-normal"
              :disabled="isLoading"
              @click="viewAllSpaces"
            >
              View all
            </button>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full">
              <thead>
                <tr
                  class="text-left text-[10px] uppercase tracking-wide text-neutral-light"
                >
                  <th class="py-1 whitespace-nowrap">S/N</th>
                  <th class="py-1">Name</th>
                  <th class="py-1">Description</th>
                </tr>
              </thead>
              <tbody class="text-xs text-primary-dark-text">
                <template v-if="isLoading">
                  <tr>
                    <td colspan="3" class="py-3 text-center text-neutral-light">
                      Loading latest spaces…
                    </td>
                  </tr>
                </template>
                <template v-else-if="latestSpaces.length">
                  <tr
                    v-for="(space, index) in latestSpaces"
                    :key="space.id"
                    class="border-t border-gray-100 hover:bg-gray-50/70 transition"
                  >
                    <td class="py-1.5 font-medium whitespace-nowrap">
                      {{ formatSerial(index) }}
                    </td>
                    <td class="py-1.5">{{ space.name }}</td>
                    <td class="py-1.5 text-neutral-light">
                      {{ space.description || "—" }}
                    </td>
                  </tr>
                </template>
                <tr v-else>
                  <td colspan="3" class="py-6">
                    <div
                      class="flex flex-col items-center justify-center gap-1.5 text-center text-xs text-neutral-light"
                    >
                      <svg
                        class="h-8 w-8 text-neutral-light/60"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <rect x="3" y="7" width="18" height="13" rx="2" />
                        <path d="M7 7V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
                        <path d="M7 12h3" />
                        <path d="M7 16h3" />
                        <path d="M14 12h3" />
                      </svg>
                      <p class="font-medium text-primary-dark-text">
                        No spaces yet
                      </p>
                      <p>Spaces you create will show up for quick access.</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section class="grid gap-3 xl:grid-cols-3">
        <div class="xl:col-span-2">
          <VoteStatisticsChart
            :title="voteChart.title"
            :subtitle="voteChart.subtitle"
            :items="voteChart.items"
          />
        </div>
        <UsageDonutChart
          :title="usageChart.title"
          :subtitle="usageChart.subtitle"
          :series="usageChart.series"
          :labels="usageChart.labels"
          :colors="usageChart.colors"
          :center-label="usageChart.centerLabel"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { DateTime } from "luxon";

import SummaryMetricCard from "@/components/dashboard/charts/SummaryMetricCard.vue";
import VoteStatisticsChart from "@/components/dashboard/charts/VoteStatisticsChart.vue";
import UsageDonutChart from "@/components/dashboard/charts/UsageDonutChart.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import { getDashboardOverview } from "@/services/dashboard";

const defaultMetricCards = [
  {
    label: "Members",
    value: 0,
    suffix: "",
    percentage: 0,
    color: "#6B4FF5",
  },
  {
    label: "Polls",
    value: 0,
    suffix: "",
    percentage: 0,
    color: "#36CFA3",
  },
  {
    label: "Spaces",
    value: 0,
    suffix: "",
    percentage: 0,
    color: "#F5B24C",
  },
  {
    label: "Pending Requests",
    value: 0,
    suffix: "",
    percentage: 0,
    color: "#FF9473",
  },
];

const defaultVoteChart = {
  title: "Vote Statistics",
  subtitle: "",
  items: [],
};

const defaultUsageChart = {
  title: "Polls and Spaces Usage",
  subtitle: "Active resources by type",
  series: [0, 0],
  labels: ["Active Polls", "Active Spaces"],
  colors: ["#6B4FF5", "#41D0B0"],
  centerLabel: "0",
};

const metricCards = ref([...defaultMetricCards]);
const latestPolls = ref([]);
const latestSpaces = ref([]);
const voteChart = ref({ ...defaultVoteChart });
const usageChart = ref({ ...defaultUsageChart });
const isLoading = ref(false);
const alertMessage = ref("");
const alertType = ref("dismiss");

const router = useRouter();

const clearAlert = () => {
  alertMessage.value = "";
  alertType.value = "dismiss";
};

const loadDashboard = async () => {
  isLoading.value = true;
  try {
    const data = await getDashboardOverview();

    metricCards.value =
      data?.metrics && data.metrics.length
        ? data.metrics
        : [...defaultMetricCards];

    latestPolls.value = data?.latestPolls ?? [];
    latestSpaces.value = data?.latestSpaces ?? [];

    voteChart.value = {
      ...defaultVoteChart,
      ...(data?.voteStatistics ?? {}),
      items: data?.voteStatistics?.items ?? [],
    };

    usageChart.value = {
      ...defaultUsageChart,
      ...(data?.usage ?? {}),
      series:
        data?.usage?.series && data.usage.series.length
          ? data.usage.series
          : defaultUsageChart.series,
      labels:
        data?.usage?.labels && data.usage.labels.length
          ? data.usage.labels
          : defaultUsageChart.labels,
      colors:
        data?.usage?.colors && data.usage.colors.length
          ? data.usage.colors
          : defaultUsageChart.colors,
      centerLabel: data?.usage?.centerLabel ?? defaultUsageChart.centerLabel,
    };

    clearAlert();
  } catch (error) {
    console.error("Failed to load dashboard data:", error);
    alertMessage.value =
      "Failed to load dashboard information. Please try again later.";
    alertType.value = "dismiss";
  } finally {
    isLoading.value = false;
  }
};

const formatSerial = (index) => (index + 1).toString().padStart(2, "0");

const formatDate = (value) => {
  if (!value) return "—";
  const date = DateTime.fromISO(value);
  return date.isValid ? date.toFormat("LLL dd, yyyy") : "—";
};

const isApprovedStatus = (status) => status && status.variant === "approved";

const statusLabel = (status) =>
  status && status.label ? status.label : "Pending";

const viewAllPolls = () => router.push("/polls");
const viewAllSpaces = () => router.push("/spaces");

onMounted(loadDashboard);
</script>
